import { formWithValidation } from '@edgebox/react-components';
import { plainToInstance, instanceToPlain } from 'class-transformer';
import { IsString, Matches, MaxLength } from 'class-validator';

export const DEFAULT_SUGGESTION_REASON = 'Suggested from onboarding page analysis.';

export type OnboardingSelectedUrlSuggestion = {
  url?: unknown;
  name?: unknown;
  reasoning?: unknown;
  confidence?: unknown;
};

export type OnboardingExtractedLink = {
  href?: unknown;
  text?: unknown;
};

export class GovernanceOnboardingPathInput {
  @IsString()
  @MaxLength(200)
  @Matches(/^\//)
  path!: string;
}

export const GovernanceOnboardingPathForm = formWithValidation<GovernanceOnboardingPathInput, GovernanceOnboardingPathInput>(
  (values) => plainToInstance(GovernanceOnboardingPathInput, values),
  (entity) => instanceToPlain(entity) as GovernanceOnboardingPathInput
);

export function normalizeConfidenceToPercent(confidence: unknown): number {
  if (typeof confidence !== 'number' || Number.isNaN(confidence) || !Number.isFinite(confidence)) {
    return 0;
  }

  const value = confidence >= 0 && confidence <= 1 ? confidence * 100 : confidence;
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function normalizeBaseUrl(baseUrl: string): string {
  const parsed = new URL(baseUrl);
  parsed.hash = '';
  parsed.search = '';
  parsed.pathname = parsed.pathname.endsWith('/') ? parsed.pathname : `${parsed.pathname}/`;
  return parsed.toString();
}

export function toRelativePath(href: string, baseUrl?: string): string | undefined {
  try {
    const parsed = baseUrl ? new URL(href, baseUrl) : new URL(href);

    if (!/^https?:$/i.test(parsed.protocol)) {
      return undefined;
    }

    return parsed.pathname || '/';
  } catch {
    return undefined;
  }
}

export function toAbsoluteUrl(path: string, baseUrl?: string): string {
  if (!baseUrl) {
    return path;
  }

  try {
    return new URL(path, baseUrl).toString();
  } catch {
    return path;
  }
}

export function normalizePathInput(input: string, baseUrl?: string): string {
  const value = String(input || '').trim();
  if (!value) {
    return '/';
  }

  if (/^https?:\/\//i.test(value)) {
    const relative = toRelativePath(value, baseUrl);
    return relative || '/';
  }

  if (value.startsWith('/')) {
    return value;
  }

  if (value.includes('.')) {
    const relative = toRelativePath(`https://${value}`, baseUrl);
    return relative || '/';
  }

  return `/${value.replace(/^\/+/, '')}`;
}

export function titleFromPath(path: string): string {
  if (path === '/') {
    return 'Home';
  }

  const normalized = path.split('?')[0].replace(/\/$/, '');
  const leaf = normalized.split('/').filter(Boolean).pop();
  if (!leaf) {
    return 'Home';
  }

  return decodeURIComponent(leaf)
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function normalizePageTitle(path: string, title?: string): string {
  const normalizedPath = normalizePathInput(path);
  if (normalizedPath === '/') {
    return 'Home';
  }

  const candidate = String(title || '').trim();
  if (candidate) {
    return candidate;
  }

  return titleFromPath(normalizedPath);
}

export function getAiReasonFromSuggestion(reasoning: unknown): string {
  if (typeof reasoning !== 'string') {
    return DEFAULT_SUGGESTION_REASON;
  }

  const reason = reasoning.trim();
  if (!reason) {
    return DEFAULT_SUGGESTION_REASON;
  }

  return reason;
}

export function getLinkNameByPath(links: unknown, baseUrl?: string): Map<string, string> {
  const result = new Map<string, string>();
  if (!Array.isArray(links)) {
    return result;
  }

  for (const rawLink of links) {
    const link = rawLink as OnboardingExtractedLink;
    if (typeof link.href !== 'string' || typeof link.text !== 'string') {
      continue;
    }

    const text = link.text.trim();
    if (!text) {
      continue;
    }

    const path = toRelativePath(link.href, baseUrl) || normalizePathInput(link.href, baseUrl);
    if (!path || result.has(path)) {
      continue;
    }

    result.set(path, text);
  }

  return result;
}

export function ensureHomeSuggestionFirst<T extends { path: string; title: string }>(items: T[], createHome: () => T): T[] {
  const normalizedItems = items.map((item) => {
    const normalizedPath = normalizePathInput(item.path);
    return {
      ...item,
      path: normalizedPath,
      title: normalizePageTitle(normalizedPath, item.title),
    };
  });

  const homeIndex = normalizedItems.findIndex((item) => item.path === '/');
  if (homeIndex === -1) {
    return [createHome(), ...normalizedItems];
  }

  const home = {
    ...normalizedItems[homeIndex],
    title: 'Home',
  };

  return [home, ...normalizedItems.filter((_, index) => index !== homeIndex)];
}

export function isUrlWithinBase(url: string, baseUrl: string): boolean {
  try {
    const normalizedBase = normalizeBaseUrl(baseUrl);
    const normalizedUrl = new URL(url, normalizedBase).toString();
    return normalizedUrl.startsWith(normalizedBase);
  } catch {
    return false;
  }
}
